/**
 * Pure view model of the «Онлайн-обучение» block (task T.F5) — the same framework-free rule
 * the other venue models follow: the `unit` Playwright project imports this in plain Node, so
 * nothing here may touch React or `import.meta.env`.
 */

import type { ApplicationStatus } from '../../api/applications';
import type { OnlinePivotRow } from '../../api/online-pivot';

/** The block's header row, in the order the api's columns map onto them. */
export const PIVOT_COLUMNS: ReadonlyArray<{ key: PivotColumnKey; label: string }> = [
  { key: 'name', label: 'Площадки' },
  { key: 'awaitingInfo', label: 'Доп. информация на ЕПГУ' },
  { key: 'awaitingLink', label: 'Ждет ссылку' },
  { key: 'approved', label: 'Одобрен' },
  { key: 'linkSent', label: 'Ссылка отправлена' },
  { key: 'total', label: 'Итого' },
];

export type PivotColumnKey = 'name' | 'awaitingInfo' | 'awaitingLink' | 'approved' | 'linkSent' | 'total';

/** The cell of a row a column key reads — the `name` column is the row's label, not a number. */
export function cellOf(row: OnlinePivotRow, key: PivotColumnKey): string | number {
  if (key === 'name') return row.name;
  return row[key];
}

/**
 * `true` for the synthetic «Онлайн» summary row — `venueId: null` is the machine marker the
 * api sends. It is the block's FIRST row and renders bold.
 */
export function isSummary(row: OnlinePivotRow): boolean {
  return row.venueId === null;
}

/** The footnote's count: online applications nobody attributed to a venue. */
export function unattributedLabel(unattributed: number, pluralRu: (count: number, forms: readonly [string, string, string]) => string): string {
  return `${unattributed} ${pluralRu(unattributed, ['заявление', 'заявления', 'заявлений'])} без площадки`;
}

/**
 * Whether the block has anything to say: the api always answers the summary row, so "nothing
 * yet" means its Итого is 0 — the block still renders (collapsed), with an empty-state line
 * instead of a table of zeros.
 */
export function hasOnlineApplications(rows: ReadonlyArray<OnlinePivotRow> | undefined): boolean {
  return (rows?.[0]?.total ?? 0) > 0;
}

// ---------------------------------------------------------------------------
// T.R6 — every cell drills into the applications register
// ---------------------------------------------------------------------------

/**
 * The application status each funnel column counts, in the api's own vocabulary — the
 * mapping that turns a CELL click into the filter the Заявления register accepts (T.R6).
 * `Итого` counts the row's whole ONLINE slice (REJECTED/NO_DATA included), so it has no
 * single status behind it and maps to `null` = "no status filter".
 */
export const PIVOT_COLUMN_STATUS: Readonly<Record<Exclude<PivotColumnKey, 'name'>, ApplicationStatus | null>> = {
  awaitingInfo: 'AWAITING_INFO',
  awaitingLink: 'WAITING_LINK',
  approved: 'EVI_PASSED',
  linkSent: 'LINK_RECEIVED',
  total: null,
};

/** The format filter every cell of this block shares — the T.F4 «Формат» chip. */
export const PIVOT_ONLINE_FORMAT = 'ONLINE';

/**
 * The `/applications` drill-down of ONE pivot cell (T.R6). Three scopes, one formula:
 *
 *   * the `Площадки` cell  → the row's scope, every status (`format=ONLINE[&venueId=N]`);
 *   * a funnel column cell → the same scope narrowed to that column's status;
 *   * the `Итого` cell     → the row's scope again (no status: it counts all six).
 *
 * `venueId` rides only on a venue row — the synthetic «Онлайн» summary is the whole
 * format, so its URL stays `format=ONLINE` (the register's unattributed slice included).
 */
export function pivotApplicationsPath(row: OnlinePivotRow, column: PivotColumnKey): string {
  const search = new URLSearchParams();
  search.set('format', PIVOT_ONLINE_FORMAT);
  if (row.venueId !== null) search.set('venueId', String(row.venueId));
  const status = column === 'name' ? null : PIVOT_COLUMN_STATUS[column];
  if (status !== null) search.set('status', status);
  return `/applications?${search.toString()}`;
}

/** The column's header, for the hover copy. */
function columnLabel(column: PivotColumnKey): string {
  return PIVOT_COLUMNS.find((candidate) => candidate.key === column)?.label ?? column;
}

/**
 * The hover affordance of a cell: what the click will filter, in the manager's words.
 * The `Площадки` cell says the row's scope only — a status column names both.
 */
export function pivotCellHint(row: OnlinePivotRow, column: PivotColumnKey): string {
  const scope = row.venueId === null ? 'Онлайн — все площадки' : `Онлайн — ${row.name}`;
  return column === 'name' ? scope : `${scope} · ${columnLabel(column)}`;
}
